import presets from './theme-presets.json';

export const THEME_PRESETS = presets;
export const DEFAULT_THEME_ID = presets[0]?.id || 'polaris-classic';
export const DEFAULT_THEME_MODE = 'dark';

const THEME_TOKEN_SET = new Set(
  THEME_PRESETS.flatMap((preset) =>
    Object.values(preset.modes || {}).flatMap((palette) => Object.keys(palette || {}))
  )
);
const THEME_TOKENS = Array.from(THEME_TOKEN_SET);

const LOGGED_IN_PATH_PREFIXES = ['/logged-in', '/onboarding'];

export function getThemePresetById(id) {
  if (!id) {
    return undefined;
  }
  return THEME_PRESETS.find((preset) => preset.id === id);
}

function getPaletteForMode(preset, mode) {
  if (!preset) {
    return undefined;
  }
  if (preset.modes?.[mode]) {
    return preset.modes[mode];
  }
  if (mode !== DEFAULT_THEME_MODE && preset.modes?.[DEFAULT_THEME_MODE]) {
    return preset.modes[DEFAULT_THEME_MODE];
  }
  return preset.modes?.light || Object.values(preset.modes || {})[0];
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') {
    return null;
  }

  const normalized = hex.replace('#', '').trim();
  if (normalized.length === 3) {
    const r = parseInt(normalized[0] + normalized[0], 16);
    const g = parseInt(normalized[1] + normalized[1], 16);
    const b = parseInt(normalized[2] + normalized[2], 16);
    if ([r, g, b].some(Number.isNaN)) {
      return null;
    }
    return { r, g, b, alpha: 1 };
  }

  if (normalized.length === 6) {
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) {
      return null;
    }
    return { r, g, b, alpha: 1 };
  }

  return null;
}

function resolveTokenValue(value, palette, seen = new Set()) {
  if (!value || typeof value !== 'string') {
    return value;
  }

  const match = value.match(/var\((--[^)\s]+)\)/);
  if (!match) {
    return value;
  }

  const token = match[1];
  if (seen.has(token)) {
    return undefined;
  }
  seen.add(token);
  const referenced = palette?.[token];
  if (!referenced) {
    return undefined;
  }
  return resolveTokenValue(referenced, palette, seen);
}

function parseColorValue(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }

  if (value.startsWith('#')) {
    return hexToRgb(value);
  }

  const rgbMatch = value.match(/rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)/i);

  if (rgbMatch) {
    const [, r, g, b, alpha = '1'] = rgbMatch;
    return {
      r: Math.min(255, parseInt(r, 10)),
      g: Math.min(255, parseInt(g, 10)),
      b: Math.min(255, parseInt(b, 10)),
      alpha: Math.min(1, Math.max(0, parseFloat(alpha))),
    };
  }

  return null;
}

function relativeLuminance({ r, g, b }) {
  const srgb = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

function contrastRatio(l1, l2) {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function getAccessibleTextColor(backgroundValue, palette, darkFallback, lightFallback) {
  const resolvedValue = resolveTokenValue(backgroundValue, palette) || backgroundValue;
  const background = parseColorValue(resolvedValue);
  const dark = parseColorValue(darkFallback);
  const light = parseColorValue(lightFallback);

  if (!background || !dark || !light) {
    return darkFallback;
  }

  const backgroundLum = relativeLuminance(background);
  const darkLum = relativeLuminance(dark);
  const lightLum = relativeLuminance(light);

  const contrastWithDark = contrastRatio(backgroundLum, darkLum);
  const contrastWithLight = contrastRatio(backgroundLum, lightLum);

  const MIN_RATIO = 4.5;

  if (contrastWithDark >= MIN_RATIO || contrastWithDark > contrastWithLight) {
    return darkFallback;
  }

  if (contrastWithLight >= MIN_RATIO) {
    return lightFallback;
  }

  return contrastWithDark >= contrastWithLight ? darkFallback : lightFallback;
}

const CONTRAST_TARGETS = {
  '--color-primary': '--text-on-primary',
  '--color-primary-strong': '--text-on-primary-strong',
  '--color-primary-light': '--text-on-primary-soft',
  '--color-success': '--text-on-success',
  '--color-success-light': '--text-on-success-soft',
  '--color-warning': '--text-on-warning',
  '--color-warning-light': '--text-on-warning-soft',
  '--color-danger': '--text-on-danger',
  '--color-danger-light': '--text-on-danger-soft',
  '--color-info': '--text-on-info',
  '--color-accent': '--text-on-accent',
  '--color-accent-light': '--text-on-accent-soft',
  '--color-background': '--text-on-background',
  '--color-surface': '--text-on-surface',
  '--color-surface-alt': '--text-on-surface-alt',
  '--surface-card': '--text-on-surface-card',
  '--surface-card-muted': '--text-on-surface-card-muted',
  '--surface-card-highlight': '--text-on-surface-card-highlight',
  '--surface-popover': '--text-on-popover',
  '--surface-popover-muted': '--text-on-popover-muted',
  '--color-overlay': '--text-on-overlay',
  '--color-overlay-strong': '--text-on-overlay-strong',
};

export function applyTheme(presetId = DEFAULT_THEME_ID, mode = DEFAULT_THEME_MODE) {
  if (typeof document === 'undefined') {
    return;
  }

  const preset = getThemePresetById(presetId) || getThemePresetById(DEFAULT_THEME_ID) || THEME_PRESETS[0];
  const palette = getPaletteForMode(preset, mode) || {};
  const root = document.documentElement;

  Object.entries(palette).forEach(([token, value]) => {
    root.style.setProperty(token, value);
  });

  const lightTextFallback = palette['--color-text-primary'] || '#ffffff';
  const darkTextFallback = palette['--color-neutral-strong'] || '#020617';

  Object.entries(CONTRAST_TARGETS).forEach(([sourceToken, targetToken]) => {
    const sourceValue = palette[sourceToken];
    if (!sourceValue) {
      return;
    }
    const accessibleColor = getAccessibleTextColor(sourceValue, palette, darkTextFallback, lightTextFallback);
    if (accessibleColor) {
      root.style.setProperty(targetToken, accessibleColor);
    }
  });

  if (mode === 'light') {
    document.body.classList.add('light-mode');
  } else {
    document.body.classList.remove('light-mode');
  }
}

export function clearThemeOverrides() {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  THEME_TOKENS.forEach((token) => {
    root.style.removeProperty(token);
  });
  document.body.classList.remove('light-mode');
}

export function storeThemeSelection(presetId, mode) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem('themePreset', presetId);
    window.localStorage.setItem('themeMode', mode);
    window.localStorage.setItem('theme', mode);
  } catch (_err) {
    // Silently ignore storage errors (private mode, etc.)
  }
}

export function getStoredThemeSelection() {
  const fallback = { presetId: DEFAULT_THEME_ID, mode: DEFAULT_THEME_MODE };
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const presetId = window.localStorage.getItem('themePreset') || fallback.presetId;
    const storedMode =
      window.localStorage.getItem('themeMode') || window.localStorage.getItem('theme') || fallback.mode;
    const mode = storedMode === 'light' ? 'light' : 'dark';
    const preset = getThemePresetById(presetId) ? presetId : fallback.presetId;
    return { presetId: preset, mode };
  } catch (_err) {
    return fallback;
  }
}

export function initializeTheme() {
  const selection = getStoredThemeSelection();
  if (typeof window !== 'undefined') {
    const { pathname } = window.location || {};
    const shouldApplyLoggedInTheme = LOGGED_IN_PATH_PREFIXES.some((prefix) =>
      pathname?.startsWith(prefix)
    );

    if (shouldApplyLoggedInTheme) {
      applyTheme(selection.presetId, selection.mode);
      return selection;
    }
  }

  clearThemeOverrides();
  return selection;
}
